import { isAxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  Caracteristicas,
  Combinacoes,
  Detalhes,
} from "shared/components/etapasCadastro/Produto";
import PageContainer from "shared/components/pagecontainer/PageContainer";
import Tabs from "shared/components/tabs/Tabs";
import { IPicture } from "shared/interfaces/IPicture";
import {
  atualizarProduto,
  criarProduto,
  sincronizarCombinacoesProduto,
} from "shared/services/api/produto.api";
import type { ProductCombinationInput } from "shared/services/api/combinacoes.api";
import { uploadImagemProduto } from "shared/services/api/uploadImagem.api";
import { cognitoGetCurrentUser } from "shared/services/aws/cognito";
import { traduzirErro } from "shared/utils/traduzirErro";
import { api } from "../../shared/services/api";
import styles from "./CdProduto.module.scss";

interface Option {
  value: string;
  label: string;
}

interface ProdutoForm {
  loja: string;
  sku?: string;
  name: string;
  descricao: string;
  price: number;
  categoria: string;
  estoque?: number;
  caracteristicas: [];
  status: "Em Estoque" | "Fora de Estoque" | "Encomendado";
  isActive: boolean;
}

export const CdProduto: React.FC = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [lojas, setLojas] = useState<Option[]>([]);
  const [categorias, setCategorias] = useState<Option[]>([]);
  const [caracteristicas, setCaracteristicas] = useState<string[]>([]);
  const [combinations, setCombinations] = useState<ProductCombinationInput[]>(
    [],
  );
  const [selectedVariationOptions, setSelectedVariationOptions] = useState<
    Record<
      string,
      import("shared/services/api/combinacoes.api").CombinationOption[]
    >
  >({});
  const [imagens, setImagens] = useState<IPicture[]>([]);
  const [salvando, setSalvando] = useState(false);
  const salvandoRef = useRef(false);
  const [aviso, setAviso] = useState("");
  const idempotencyKeyRef = useRef<string | null>(null);

  const {
    register,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<ProdutoForm>({
    mode: "onChange",
    defaultValues: {
      status: "Em Estoque",
      isActive: true,
    },
  });

  const loja = watch("loja");
  const name = watch("name");
  const categoria = watch("categoria");
  const descricao = watch("descricao");
  const preco = watch("price");
  const unidades = watch("estoque");

  const podeSalvar =
    Boolean(loja?.trim()) &&
    Boolean(name?.trim()) &&
    Boolean(descricao?.trim()) &&
    Boolean(categoria) &&
    Number(preco) > 0 &&
    Number(unidades) > 0;

  // =========================================
  // IDEMPOTENCY KEY
  // =========================================

  useEffect(() => {
    idempotencyKeyRef.current = crypto.randomUUID();

    return () => {
      idempotencyKeyRef.current = null;
    };
  }, []);

  // =========================================
  // LOJAS
  // =========================================

  useEffect(() => {
    async function carregarLojas() {
      try {
        const response = await api.get("/stores/mine");

        setLojas(
          response.data.map((loja: any) => ({
            value: loja.id,
            label: loja.name,
          })),
        );
      } catch {
        setLojas([]);
      }
    }

    carregarLojas();
  }, []);

  // =========================================
  // CATEGORIAS
  // =========================================

  useEffect(() => {
    async function carregarCategorias() {
      try {
        const response = await api.get("/categories");

        const categoriasFormatadas = response.data.map((categoria: any) => ({
          value: categoria.id,
          label: categoria.name,
        }));

        setCategorias(categoriasFormatadas);
      } catch {
        setCategorias([]);
      }
    }

    carregarCategorias();
  }, []);

  // =========================================
  // CANCELAR
  // =========================================

  const cancelarCadastro = () => {
    idempotencyKeyRef.current = null;
    navigate("/meusprodutos");
  };

  // =========================================
  // SUBMIT
  // =========================================

  const onSubmit = async () => {
    if (salvandoRef.current) {
      return;
    }

    if (imagens.length === 0 || caracteristicas.length === 0) {
      setActive(1);

      if (imagens.length === 0 && caracteristicas.length === 0) {
        setAviso(
          "Antes de cadastrar o produto, adicione pelo menos uma imagem e uma característica.",
        );
      } else if (imagens.length === 0) {
        setAviso("Adicione pelo menos uma imagem do produto.");
      } else {
        setAviso("Adicione pelo menos uma característica do produto.");
      }

      return;
    }

    setAviso("");

    if (!podeSalvar) {
      return;
    }

    const idempotencyKey = idempotencyKeyRef.current;

    if (!idempotencyKey) {
      setAviso(
        "Não foi possível iniciar o cadastro do produto. Abra a tela novamente.",
      );
      return;
    }

    try {
      salvandoRef.current = true;
      setSalvando(true);

      const user = await cognitoGetCurrentUser();

      if (!user) {
        return;
      }

      const data = getValues();

      const lojaSelecionada = lojas.find((l) => l.value === data.loja);

      const produtoId = await criarProduto(
        {
          uidDono: user.userId,
          lojaId: data.loja,
          lojaNome: lojaSelecionada?.label ?? "",
          name: data.name,
          descricao: data.descricao,
          price: data.price,
          categoriaId: data.categoria,
          categoriaNome: data.categoria,
          caracteristicas,
          estoque: data.estoque,
          status: data.status,
          isActive: data.isActive,
          gallery: [],
        },
        idempotencyKey,
      );

      if (combinations.length > 0) {
        await sincronizarCombinacoesProduto(produtoId, combinations);
      }

      const novasImagens = imagens.filter((img) => img.file);

      if (novasImagens.length > 0) {
        const uploads = await Promise.all(
          novasImagens.map((img) =>
            uploadImagemProduto(produtoId, img.file as File),
          ),
        );

        await atualizarProduto(produtoId, {
          gallery: uploads.map((upload) => upload.key),
        });
      }

      idempotencyKeyRef.current = null;

      navigate("/produto-cadastrado", {
        state: { produto: data.name },
      });
    } catch (error) {
      alert(traduzirErro(error));

      if (isAxiosError(error)) {
        return;
      }
    } finally {
      salvandoRef.current = false;
      setSalvando(false);
    }
  };

  return (
    <PageContainer title="Cadastro de Produto" showDesktopSearch={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.page}>
          {aviso && (
            <div className={styles.aviso}>
              <div className={styles.icon}>⚠️</div>
              <div>
                <strong>Quase lá!</strong>
                <p>{aviso}</p>
              </div>
            </div>
          )}

          <Tabs active={active} setActive={setActive} />

          {active === 0 && (
            <Detalhes
              modo="cadastro"
              register={register}
              errors={errors}
              lojas={lojas}
              categorias={categorias}
              watch={watch}
              setValue={setValue}
              status={watch("status")}
              isActive={watch("isActive")}
              onChangeStatus={(v) => setValue("status", v)}
              onChangeIsActive={(v) => setValue("isActive", v)}
            />
          )}

          {active === 1 && (
            <Caracteristicas
              register={register}
              errors={errors}
              imagens={imagens}
              setImagens={setImagens}
              caracteristicas={caracteristicas}
              setCaracteristicas={setCaracteristicas}
              categoryId={categoria ?? ""}
              selectedVariationOptions={selectedVariationOptions}
              setSelectedVariationOptions={setSelectedVariationOptions}
            />
          )}

          {active === 2 && (
            <Combinacoes
              selectedVariationOptions={selectedVariationOptions}
              combinations={combinations}
              setCombinations={setCombinations}
            />
          )}

          <div className={styles.containerBtn}>
            {active === 0 ? (
              <div>
                <button
                  type="button"
                  className={styles.btnCancelar}
                  onClick={cancelarCadastro}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  disabled={!podeSalvar}
                  className={`${styles.btnSalvar} ${
                    podeSalvar ? styles.btnSalvarAtivo : styles.btnSalvarInativo
                  }`}
                  onClick={() => setActive(1)}
                >
                  Avançar
                </button>
              </div>
            ) : active === 1 ? (
              <div>
                <button
                  type="button"
                  className={styles.btnCancelar}
                  onClick={() => setActive(0)}
                >
                  Voltar
                </button>

                <button
                  key="avancar-combinacoes"
                  type="button"
                  onClick={() => setActive(2)}
                  disabled={!podeSalvar || salvando}
                  className={`${styles.btnSalvar} ${
                    podeSalvar && !salvando
                      ? styles.btnSalvarAtivo
                      : styles.btnSalvarInativo
                  }`}
                >
                  Avançar
                </button>
              </div>
            ) : (
              <div>
                <button
                  type="button"
                  className={styles.btnCancelar}
                  onClick={() => setActive(1)}
                >
                  Voltar
                </button>
                <button
                  key="cadastrar-produto"
                  type="submit"
                  disabled={!podeSalvar || salvando}
                  className={`${styles.btnSalvar} ${
                    podeSalvar && !salvando
                      ? styles.btnSalvarAtivo
                      : styles.btnSalvarInativo
                  }`}
                >
                  {salvando ? "Cadastrando..." : "Cadastrar Produto"}
                </button>
              </div>
            )}
          </div>
        </div>
      </form>
    </PageContainer>
  );
};
